import { useState, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import PageSectionTable from '../../../../shared/components/layouts/PageSectionTable'
import FormDrawer from '../../../../shared/components/drawers/FormDrawer'
import LabelForm from './LabelForm'
import VariableForm from './VariableForm'
import { useConfirmationDialog } from '../../../../system/ConfirmationDialogProvider'

function ServiceConfigurationTab({
  labels,
  labelsLoading,
  createLabel,
  updateLabel,
  destroyLabel,
  variables,
  variablesLoading,
  createVariable,
  updateVariable,
  destroyVariable,
}) {
  const { openConfirmationDialog } = useConfirmationDialog()
  const [labelDrawer, setLabelDrawer] = useState({ open: false, label: null })
  const [variableDrawer, setVariableDrawer] = useState({ open: false, variable: null })

  // Label handlers
  const handleOpenLabelDrawer = useCallback((label = null) => {
    setLabelDrawer({ open: true, label })
  }, [])

  const handleCloseLabelDrawer = useCallback(() => {
    setLabelDrawer({ open: false, label: null })
  }, [])

  const handleDeleteLabel = useCallback((label) => {
    openConfirmationDialog({
      title: <FormattedMessage id="labels.delete.title" />,
      message: <FormattedMessage id="labels.delete.message" />,
      onConfirm: () => destroyLabel(label.id),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroyLabel])

  // Variable handlers
  const handleOpenVariableDrawer = useCallback((variable = null) => {
    setVariableDrawer({ open: true, variable })
  }, [])

  const handleCloseVariableDrawer = useCallback(() => {
    setVariableDrawer({ open: false, variable: null })
  }, [])

  const handleDeleteVariable = useCallback((variable) => {
    openConfirmationDialog({
      title: <FormattedMessage id="variables.delete.title" />,
      message: <FormattedMessage id="variables.delete.message" />,
      onConfirm: () => destroyVariable(variable.id),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroyVariable])

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          <FormattedMessage id="dockerServices.sections.labels" />
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleOpenLabelDrawer()}
        >
          <FormattedMessage id="dockerServices.show.addLabel" />
        </Button>
      </Box>
      {labelsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <PageSectionTable
          items={labels}
          emptyId="dockerServices.show.labelsEmpty"
          keyField="key"
          valueField="value"
          onItemEdit={handleOpenLabelDrawer}
          onItemDelete={handleDeleteLabel}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
        <Typography variant="h6">
          <FormattedMessage id="dockerServices.sections.variables" />
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleOpenVariableDrawer()}
        >
          <FormattedMessage id="dockerServices.show.addVariable" />
        </Button>
      </Box>
      {variablesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <PageSectionTable
          items={variables}
          emptyId="dockerServices.show.variablesEmpty"
          keyField="key"
          valueField="value"
          maskValues
          onItemEdit={handleOpenVariableDrawer}
          onItemDelete={handleDeleteVariable}
        />
      )}

      <FormDrawer
        open={labelDrawer.open}
        onClose={handleCloseLabelDrawer}
        titleId={labelDrawer.label ? 'labels.edit.title' : 'labels.create.title'}
      >
        <LabelForm
          label={labelDrawer.label}
          createLabel={createLabel}
          updateLabel={updateLabel}
          onSuccess={handleCloseLabelDrawer}
          onCancel={handleCloseLabelDrawer}
        />
      </FormDrawer>

      <FormDrawer
        open={variableDrawer.open}
        onClose={handleCloseVariableDrawer}
        titleId={variableDrawer.variable ? 'variables.edit.title' : 'variables.create.title'}
      >
        <VariableForm
          variable={variableDrawer.variable}
          createVariable={createVariable}
          updateVariable={updateVariable}
          onSuccess={handleCloseVariableDrawer}
          onCancel={handleCloseVariableDrawer}
        />
      </FormDrawer>
    </>
  )
}

export default ServiceConfigurationTab
